import { useEffect, useState } from 'react';

// Click-and-drag horizontal scrolling for an overflow-x container. Returns a ref
// to attach to that element. A drag past a few pixels scrolls; a real drag also
// swallows the trailing click (captured) so it doesn't trigger a cell/image
// click underneath. Give the element `cursor-grab` for the idle hint.
// A callback ref, not a ref object: the element is often mounted later than its
// component (a table behind a toggle), and an effect run once at mount would find
// nothing and never look again.
export function useDragScroll<T extends HTMLElement>() {
  const [el, ref] = useState<T | null>(null);

  useEffect(() => {
    if (!el) return;

    let down = false;
    let moved = false;
    let startX = 0;
    let startLeft = 0;

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      // Not from a field: a press in one is to type or select, and a drag there is a selection.
      if ((e.target as HTMLElement).closest('input, textarea, select')) return;
      down = true;
      moved = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      el.style.cursor = 'grabbing';
      el.style.userSelect = 'none';
    };
    const onMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 4) moved = true;
      el.scrollLeft = startLeft - dx;
    };
    const onUp = () => {
      down = false;
      el.style.cursor = '';
      el.style.userSelect = '';
    };
    // Capture phase so a drag's click is swallowed before it reaches a cell.
    const onClick = (e: MouseEvent) => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        moved = false;
      }
    };

    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    el.addEventListener('click', onClick, true);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      el.removeEventListener('click', onClick, true);
    };
  }, [el]);

  return ref;
}
